import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser';
import { authenticationClientProvider, msalConfig } from '@config';

import { loadedAppInsights } from '../appInsights/appInsights';

export class AuthService {
  appInsights;
  account: AccountInfo | null = null;
  msalApplication: PublicClientApplication | null = null;
  msalApplicationScope = {
    scopes: msalConfig.scopes,
  };
  tokenScopes = msalConfig.scopes;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.appInsights = loadedAppInsights;
    this.msalApplication = authenticationClientProvider;
  }

  private async ensureInitialized() {
    if (this.initialized) return;
    if (!this.initPromise) {
      this.initPromise = this.doInitialize();
    }
    await this.initPromise;
  }

  private async doInitialize() {
    if (this.msalApplication) {
      await this.msalApplication.initialize();
      await this.msalApplication.handleRedirectPromise();
    }
    const currentAccounts = this.msalApplication?.getAllAccounts() ?? [];
    const account = currentAccounts.length >= 1 ? currentAccounts[0] : null;
    if (currentAccounts.length > 1) {
      this.msalApplication?.setActiveAccount(account);
    }
    if (account) {
      this.account = account;
    }
    this.initialized = true;
  }

  async signIn() {
    await this.ensureInitialized();
    this.msalApplication?.loginRedirect(this.msalApplicationScope);
  }

  async signOut() {
    await this.ensureInitialized();
    if (!this.account) {
      // no cached login to signout
      return;
    }

    const logoutRequestPayload = {
      account: this.account,
    };

    if (this.msalApplication) {
      this.msalApplication.logoutRedirect(logoutRequestPayload);
    }
  }

  async handlePageLoadEvent() {
    await this.ensureInitialized();

    return this.msalApplication?.handleRedirectPromise().then((authResult: any) => {
      this.handleRedirectResponse(authResult);
    });
  }

  async handleRedirectResponse(authResult: { account: null } | null) {
    if (authResult !== null) {
      this.account = authResult.account;
    } else {
      const currentAccounts = this.msalApplication?.getAllAccounts(); // Add null check
      if (currentAccounts === undefined || currentAccounts === null) {
        return;
      } else if (currentAccounts?.length > 1) {
        console.warn(`more than one msal account found. Forcing to first ${currentAccounts[0]}`);
        this.account = currentAccounts[0];
      } else if (currentAccounts?.length === 1) {
        this.account = currentAccounts[0];
      }
    }
  }

  async getAccount() {
    await this.ensureInitialized();
    const currentAccounts = this.msalApplication?.getAllAccounts(); // Add null check
    if (currentAccounts === undefined || currentAccounts === null) {
      this.signIn();
    } else if (currentAccounts.length > 1) {
      console.warn(`more than one msal account found. Forcing to first ${currentAccounts[0]}`);
      this.account = currentAccounts[0];
    } else if (currentAccounts.length === 1) {
      this.account = currentAccounts[0];
    }

    return this.account;
  }

  async getToken(forceRefresh = false) {
    await this.ensureInitialized();
    const account = this.account ?? (await this.getAccount());
    if (account) {
      if (forceRefresh) {
        console.warn('token refresh requested');
      }
    } else {
      console.warn('no account found, redirecting to login');
      await this.signIn();

      return undefined;
    }
    const tokenRequest: SilentRequest = {
      account: account ?? undefined,
      scopes: this.tokenScopes,
      forceRefresh: forceRefresh,
    };

    try {
      return await this.msalApplication?.acquireTokenSilent(tokenRequest);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('Silent token acquisition failed, redirecting to login');
        await this.msalApplication?.acquireTokenRedirect({
          scopes: this.tokenScopes,
          account: account ?? undefined,
        });

        return undefined;
      }
      throw error;
    }
  }
}
const authConfigInstance = new AuthService();
export default authConfigInstance;
